import type { CoviActionItem, CoviChatItem } from "../model/types"
import { buildActionTitle } from "./actions"
import { isStackableAction } from "./actionStackPolicy"
import { toolState, toolTraceCounts } from "./toolSteps"

type CoviToolItem = Extract<CoviChatItem, { kind: "tool" }>

type TranscriptRenderUnit =
  | { kind: "item"; id: string; item: CoviChatItem; isLast: boolean }
  | { kind: "tool-group"; id: string; items: CoviToolItem[] }
  | { kind: "action-stack"; id: string; items: CoviActionItem[] }

/** Presentation only: preserve the full transcript for Sofia's next turn. */
function visibleTranscriptItems(items: CoviChatItem[]): CoviChatItem[] {
  const actions = items.filter(
    (item): item is CoviActionItem => item.kind === "action",
  )
  const representedActions = new Set(
    actions.map((item) => item.proposal.action_id ?? item.proposal.proposal_id),
  )
  const visible = items.filter(
    (item) =>
      (item.kind !== "assistant" && item.kind !== "tool") ||
      !item.confirmationReceiptFor ||
      !representedActions.has(item.confirmationReceiptFor),
  )
  const children = new Map<string, CoviChatItem[]>()
  const roots: CoviChatItem[] = []
  for (const item of visible) {
    const parentId =
      item.kind === "action" ? item.confirmationParentId : undefined
    // Only move explicitly linked follow-ups to an earlier parent. This keeps
    // orphaned or malformed historical records visible and cannot create cycles.
    const parentIndex = parentId
      ? visible.findIndex(
          (candidate) =>
            candidate.kind === "action" &&
            (candidate.proposal.action_id ?? candidate.proposal.proposal_id) ===
              parentId,
        )
      : -1
    if (parentId && parentIndex >= 0 && parentIndex < visible.indexOf(item)) {
      let anchor = visible[parentIndex] as CoviActionItem
      for (
        let index = parentIndex + 1;
        index < visible.indexOf(item);
        index += 1
      ) {
        const sibling = visible[index]
        if (
          sibling.kind !== "action" ||
          !isStackableAction(anchor) ||
          !isStackableAction(sibling) ||
          sibling.proposal.kind !== anchor.proposal.kind ||
          !anchor.proposalBatchId ||
          sibling.proposalBatchId !== anchor.proposalBatchId
        ) {
          break
        }
        anchor = sibling
      }
      const anchorId = anchor.proposal.action_id ?? anchor.proposal.proposal_id
      children.set(anchorId, [...(children.get(anchorId) ?? []), item])
    } else {
      roots.push(item)
    }
  }
  const emitted = new Set<CoviChatItem>()
  const withChildren = (item: CoviChatItem): CoviChatItem[] => {
    if (emitted.has(item)) {
      return []
    }
    emitted.add(item)
    const next =
      item.kind === "action"
        ? (children.get(item.proposal.action_id ?? item.proposal.proposal_id) ??
          [])
        : []
    return [item, ...next.flatMap(withChildren)]
  }
  return [...roots.flatMap(withChildren), ...visible.flatMap(withChildren)]
}

export function groupTranscriptItems(
  items: CoviChatItem[],
  nestedActionIds?: ReadonlySet<string>,
): TranscriptRenderUnit[] {
  const units: TranscriptRenderUnit[] = []
  let run: CoviToolItem[] = []
  const flush = () => {
    if (run.length > 0) {
      units.push({ kind: "tool-group", id: `tools_${run[0].id}`, items: run })
      run = []
    }
  }
  const visibleItems = visibleTranscriptItems(items).filter(
    (item) => !nestedActionIds?.has(item.id),
  )
  visibleItems.forEach((item, index) => {
    if (item.kind === "tool") {
      run.push(item)
      return
    }
    flush()
    const previous = units.at(-1)
    if (item.kind === "action" && isStackableAction(item)) {
      const prior =
        previous?.kind === "action-stack"
          ? previous.items.at(-1)
          : previous?.kind === "item" && previous.item.kind === "action"
            ? previous.item
            : undefined
      // Only adjacent proposals emitted together belong to the same approval.
      if (
        prior &&
        isStackableAction(prior) &&
        prior.proposal.kind === item.proposal.kind &&
        prior.proposalBatchId &&
        prior.proposalBatchId === item.proposalBatchId
      ) {
        if (previous?.kind === "action-stack") {
          previous.items.push(item)
        } else {
          units.splice(-1, 1, {
            kind: "action-stack",
            id: `actions_${prior.id}`,
            items: [prior, item],
          })
        }
        return
      }
    }
    units.push({
      kind: "item",
      id: item.id,
      item,
      isLast: index === visibleItems.length - 1,
    })
  })
  flush()
  return units
}

export function serializeChatItem(item: CoviChatItem): string {
  switch (item.kind) {
    case "user":
      return `**You:** ${item.text}`
    case "assistant":
      return `**Coval:** ${item.text}`
    case "tool": {
      const status = {
        failed: "error",
        running: "in progress",
        completed: "done",
        reused: "reused",
        unknown: "unknown",
      }[toolState(item)]
      const suffix = item.summary ? `: ${item.summary}` : ""
      const omittedCount =
        Object.values(toolTraceCounts([item])).reduce(
          (sum, count) => sum + count,
          0,
        ) - 1
      const omitted =
        omittedCount > 0
          ? `; ${omittedCount} additional steps omitted`
          : item.traceTruncated
            ? "; additional live activity omitted"
            : ""
      return `_[tool: ${item.name} — ${status}${suffix}${omitted}]_`
    }
    case "action": {
      const title = buildActionTitle(item.proposal)
      const detail =
        item.status === "done" && item.detail ? `: ${item.detail}` : ""
      return [
        `_[action: ${title} — ${item.status}${detail}]_`,
        item.error,
        item.feedback?.text,
        item.feedback?.warning,
      ]
        .filter(Boolean)
        .join("\n\n")
    }
    case "analysis": {
      const analysis = item.analysis
      const result =
        analysis.status === "succeeded"
          ? `Result: ${String(analysis.value)}${analysis.explanation ? `\n${analysis.explanation}` : ""}`
          : analysis.status === "failed" || analysis.status === "unavailable"
            ? analysis.message
            : null
      return [
        `Audio analysis ${analysis.analysis_id} — ${analysis.status}`,
        result,
      ]
        .filter(Boolean)
        .join("\n\n")
    }
    case "error":
      return `_[Error: ${item.text}]_`
    default:
      return ""
  }
}

export function serializeTranscript(
  items: CoviChatItem[],
  now: () => Date = () => new Date(),
): string {
  const header = `Sofia (Coval agent) transcript — ${now().toISOString()}\n\n`
  const body = visibleTranscriptItems(items)
    .map(serializeChatItem)
    .filter(Boolean)
    .join("\n\n")
  return `${header}${body}\n`
}
